import React, { useEffect } from 'react';
import { budgetQuotationService } from '../services/budgetQuotationService';
import { useAuthStore } from '@/store/authStore';

export const QuotationInfo = ({ details, onChange, title = "QUOTATION", numberLabel = "Quotation No." }) => {
  const { user } = useAuthStore();
  const isOrdering = title === 'ORDERING';

  useEffect(() => {
    // 1. Generate Date
    const today = new Date();
    const dateStr = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;

    if (!details.date) {
      onChange('date', dateStr);
    }

    if (user) {
      const loggedInUserName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();
      if (loggedInUserName) {
        if (isOrdering) {
          if (details.referenceDesigner !== loggedInUserName) {
            onChange('referenceDesigner', loggedInUserName);
          }
        } else if (!details.referenceDesigner && !details.designer) {
          onChange('referenceDesigner', loggedInUserName);
        }
      }
    }
  }, [user, isOrdering]);

  useEffect(() => {
    if (title === 'ORDERING' || details?.quotationType === 'Ordering' || details?.docType === 'Order' || details?.category) {
      return;
    }
    // 2. Fetch Quotation No from backend sequence if missing or if letter code mismatches
    const isInitial = Boolean(details?.isInitial || details?.type === 'initial_quotation');
    const expectedLetter = isInitial ? 'I' : 'F';
    const hasWrongLetter = details.quotationNo && typeof details.quotationNo === 'string' && details.quotationNo.startsWith('VK20') && details.quotationNo.charAt(6) !== expectedLetter;

    if (!details.quotationNo || details.quotationNo === '-' || details.quotationNo === 'Quotation number' || hasWrongLetter || (!details._id && details.quotationNo === '')) {
      budgetQuotationService.getNextQuotationNo(isInitial ? 'initial' : 'final')
        .then(res => {
          if (res?.data?.quotationNo && res.data.quotationNo !== details.quotationNo) {
            onChange('quotationNo', res.data.quotationNo);
          }
        })
        .catch(err => {
          console.error("Failed to fetch next quotation number:", err);
          const currentYear = new Date().getFullYear();
          if (!details.quotationNo || details.quotationNo === '-') {
            onChange('quotationNo', `VK${currentYear}${expectedLetter}00001`);
          }
        });
    }
  }, [details._id, details.isInitial, details.quotationNo, details.type, title, details.category]);

  const formatValue = (val) => {
    if (!val) return '';
    if (typeof val === 'string' || typeof val === 'number') return val;
    if (typeof val === 'object') {
      return val.name || val.fullName || val.customerName || val.customId ||
        ((val.firstName || val.lastName) ? `${val.firstName || ''} ${val.lastName || ''}`.trim() : '') ||
        val.email || val.phone || '';
    }
    return String(val);
  };

  const loggedInName = user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-lg overflow-hidden mb-4">
      {/* Header */}
      <div className="bg-black text-white text-center py-1.5 font-bold text-sm tracking-wide uppercase">
        {title}
      </div>

      {/* Grid container */}
      <div className="grid grid-cols-[auto_1fr_auto_1fr] text-sm border-t border-black bg-white">

        {/* Row 1 */}
        <div className="border-r border-b border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">Customer Name & Ph. : -</span>
        </div>
        <div className="border-r border-b border-black px-1 py-[3px] min-w-[150px]">
          <input
            type="text"
            value={formatValue(details.projectName)}
            readOnly={isOrdering}
            onChange={(e) => !isOrdering && onChange('projectName', e.target.value)}
            className={`w-full h-full bg-transparent outline-none placeholder:text-slate-300 text-[13px] ${isOrdering ? 'text-slate-700 cursor-default' : ''}`}
            placeholder="Name & Phone"
          />
        </div>
        <div className="border-r border-b border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">Date :</span>
        </div>
        <div className="border-b border-black px-1 py-[3px] min-w-[150px]">
          <input
            type="text"
            value={formatValue(details.date)}
            readOnly
            className="w-full h-full bg-transparent outline-none text-slate-700 text-center cursor-default text-[13px]"
            placeholder="Date"
          />
        </div>

        {/* Row 2 */}
        <div className="border-r border-b border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">Location</span>
        </div>
        <div className="border-r border-b border-black px-1 py-[3px]">
          <input
            type="text"
            value={formatValue(details.site)}
            readOnly={isOrdering}
            onChange={(e) => !isOrdering && onChange('site', e.target.value)}
            className={`w-full h-full bg-transparent outline-none placeholder:text-slate-300 text-[13px] ${isOrdering ? 'text-slate-700 cursor-default' : ''}`}
            placeholder="Location"
          />
        </div>
        <div className="border-r border-b border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">{numberLabel}</span>
        </div>
        <div className="border-b border-black px-1 py-[3px]">
          <input
            type="text"
            value={formatValue(details.quotationNo)}
            readOnly
            className="w-full h-full bg-transparent outline-none text-slate-700 text-center cursor-default text-[13px]"
            placeholder="Quotation number"
          />
        </div>

        {/* Row 3 */}
        <div className="border-r border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">Contact Ref:</span>
        </div>
        <div className="border-r border-black px-1 py-[3px]">
          <input
            type="text"
            value={formatValue(details.contactRef)}
            readOnly={isOrdering}
            onChange={(e) => !isOrdering && onChange('contactRef', e.target.value)}
            className={`w-full h-full bg-transparent outline-none placeholder:text-slate-300 text-[13px] ${isOrdering ? 'text-slate-700 cursor-default' : ''}`}
            placeholder="Contact Reference"
          />
        </div>
        <div className="border-r border-black px-1 py-[3px] flex items-center bg-slate-50/50">
          <span className="font-medium text-[13px] whitespace-nowrap text-slate-700">Reference / Designer</span>
        </div>
        <div className="px-1 py-[3px]">
          <input
            type="text"
            value={isOrdering ? (loggedInName || formatValue(details.referenceDesigner || details.designer)) : formatValue(details.referenceDesigner || details.designer)}
            readOnly={isOrdering}
            onChange={(e) => !isOrdering && onChange('referenceDesigner', e.target.value)}
            className={`w-full h-full bg-transparent outline-none placeholder:text-slate-300 text-center text-[13px] ${isOrdering ? 'text-slate-700 cursor-default' : ''}`}
            placeholder="Ref or designer"
          />
        </div>
      </div>
    </div>
  );
};
